import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import DerivativesInteractiveBookCTA from '../components/DerivativesInteractiveBookCTA';
import VittaDerivativesPathways from '../components/VittaDerivativesPathways';

const DerivativesStudyGuide = () => {
  // Book and Course data
  const studyMaterials = {
    book: {
      title: "Printed Derivatives Study Guide",
      description: "Comprehensive exam-focused study guide for the CIRO Derivatives Exam.",
      price: "299.99",
      link: "https://a.co/d/2QDVP8K"
    },
    interactiveBook: {
      title: "Derivatives Interactive Study Guide",
      description: "Master the Derivatives exam with interactive flashcards, quizzes, and visual learning tools across all 8 elements.",
      price: "49.99",
      link: "/books/derivatives-interactive"
    },
    course: {
      title: "Derivatives Exam Prep Course",
      price: "29.99",
      link: "https://vittafinance.mykajabi.com/offers/mxKYcWsw"
    }
  };

  const learningPath = [
    {
      phase: "Phase 1: Getting Started",
      description: "Understand the Derivatives exam basics and create your study plan",
      duration: "Week 1",
      articles: [
        {
          title: "Derivatives Exam Overview: Complete Guide",
          slug: "derivatives-exam-overview-complete-guide",
          description: "Exam structure, element weightings, and study strategy"
        },
        {
          title: "What is the CIRO Exam? Beginner's Guide",
          slug: "what-is-ciro-exam-beginners-guide",
          description: "Understanding CIRO exams and career paths"
        },
        {
          title: "CIRO Exam Study Schedule: 30, 60, 90 Day Plans",
          slug: "ciro-exam-study-schedule-plan",
          description: "Choose the right study timeline"
        }
      ]
    },
    {
      phase: "Phase 2: Element 1 - The Client Relationship (7%)",
      description: "Master KYC, Client Focused Reforms, and Best Interest Standard",
      duration: "Week 1-2",
      articles: [
        {
          title: "KYC, Client Focused Reforms & Best Interest Standard",
          slug: "derivatives-exam-kyc-client-focused-reforms",
          description: "Risk Tolerance vs Capacity, TCP, Eligible Derivatives Party"
        }
      ],
      topics: [
        "Know Your Client (KYC) for derivatives",
        "Risk Tolerance vs. Risk Capacity",
        "Best Interest Standard under CFR",
        "Eligible Derivatives Party (EDP)",
        "Trusted Contact Person (TCP)",
        "RR vs. IR roles and responsibilities"
      ]
    },
    {
      phase: "Phase 3: Element 2 - Regulatory Documentation (8%)",
      description: "Learn account documentation and client classification",
      duration: "Week 2",
      articles: [
        {
          title: "Regulatory Documentation & Client Classification",
          slug: "derivatives-exam-regulatory-documentation",
          description: "Trading agreements, margin agreements, hedge agreements"
        }
      ],
      topics: [
        "Derivatives Trading Agreement",
        "Risk Disclosure Statement",
        "Margin Agreement requirements",
        "Hedge Agreement for reduced margin",
        "Retail vs. Institutional classification",
        "Position and Concentration Reporting"
      ]
    },
    {
      phase: "Phase 4: Element 3 - Types & Features (18%)",
      description: "Master options, futures, forwards, swaps, and exotic derivatives",
      duration: "Week 3-4",
      articles: [
        {
          title: "Types & Features: Options, Futures, Swaps & Forwards",
          slug: "derivatives-exam-types-features-options-futures",
          description: "Listed vs OTC, calls/puts, American/European, moneyness"
        }
      ],
      highlight: true,
      topics: [
        "Listed vs. OTC derivatives",
        "Call and Put options mechanics",
        "American vs. European style",
        "Moneyness: ITM, ATM, OTM",
        "Futures vs. Forwards",
        "Interest Rate, Equity, and Currency Swaps",
        "Exotic Options: Barrier, Asian, Binary",
        "Physical vs. Cash Settlement"
      ]
    },
    {
      phase: "Phase 5: Element 4 - Derivative Pricing (18%)",
      description: "Master The Greeks, Black-Scholes fundamentals, and pricing models",
      duration: "Week 4-5",
      articles: [
        {
          title: "Pricing, The Greeks & Black-Scholes Fundamentals",
          slug: "derivatives-exam-pricing-greeks-black-scholes",
          description: "Delta, Gamma, Theta, Vega, futures pricing, Put-Call Parity"
        }
      ],
      highlight: true,
      topics: [
        "Intrinsic Value vs. Time Value",
        "Six Variables of Option Pricing",
        "Delta: Price sensitivity",
        "Gamma: Delta's rate of change",
        "Theta: Time decay",
        "Vega: Volatility sensitivity",
        "Cost of Carry model",
        "Contango vs. Backwardation",
        "Put-Call Parity"
      ]
    },
    {
      phase: "Phase 6: Element 5 - Clearing & Settlement (17%)",
      description: "Learn CDCC, margin requirements, and mark-to-market process",
      duration: "Week 5-6",
      articles: [
        {
          title: "CDCC, Clearing, Settlement & Mark-to-Market",
          slug: "derivatives-exam-cdcc-clearing-settlement",
          description: "Novation, margin types, daily settlement, exercise/assignment"
        }
      ],
      topics: [
        "CDCC Central Counterparty",
        "Novation and guarantee",
        "Initial, Maintenance, Variation Margin",
        "Daily Mark-to-Market process",
        "Order types: Market, Limit, Stop",
        "UMIR rules and Best Execution",
        "Exercise and Assignment",
        "T+1 Settlement"
      ]
    },
    {
      phase: "Phase 7: Element 6 - Trading Strategies (22%)",
      description: "Master spreads, straddles, hedging, and arbitrage strategies",
      duration: "Week 6-8",
      articles: [
        {
          title: "Trading Strategies: Spreads, Straddles & Hedging",
          slug: "derivatives-exam-trading-strategies-spreads-hedging",
          description: "Long call/put, covered call, vertical spreads, beta hedging"
        }
      ],
      highlight: true,
      topics: [
        "Long Call and Long Put",
        "Covered Call strategy",
        "Protective Put (insurance)",
        "Bull Call Spread / Bear Put Spread",
        "Calendar (Time) Spread",
        "Long Straddle / Short Straddle",
        "Strangle strategy",
        "Short Hedge / Long Hedge",
        "Beta Hedging formula",
        "Duration Hedging",
        "Cash and Carry Arbitrage"
      ]
    },
    {
      phase: "Phase 8: Elements 7 & 8 - Ethics & Conduct (10%)",
      description: "Market integrity rules and professional standards",
      duration: "Week 8-9",
      articles: [
        {
          title: "Market Integrity & Manipulation Rules",
          slug: "derivatives-exam-market-integrity-manipulation",
          description: "Wash trading, spoofing, layering, front running"
        },
        {
          title: "Standards of Conduct & Conflicts of Interest",
          slug: "derivatives-exam-standards-conduct-conflicts",
          description: "Conflict management, PFD, OBA, referral arrangements"
        }
      ],
      topics: [
        "Wash Trading prohibition",
        "Spoofing and Layering",
        "Marking the Close",
        "Insider Trading and Tipping",
        "Front Running prohibition",
        "Conflict management hierarchy",
        "Personal Financial Dealings",
        "Outside Business Activities",
        "Duty to Warn"
      ]
    },
    {
      phase: "Phase 9: Math & Final Review",
      description: "Essential calculations and exam preparation",
      duration: "Week 9-10",
      articles: [
        {
          title: "Derivatives Exam Math: Formulas & Calculations",
          slug: "derivatives-exam-math-formulas-calculations",
          description: "Break-evens, spread P/L, beta hedging, Greeks"
        },
        {
          title: "Free CIRO Practice Questions",
          slug: "free-ciro-practice-questions",
          description: "Test your knowledge before exam day"
        }
      ],
      topics: [
        "Intrinsic value calculations",
        "Break-even formulas",
        "Spread profit/loss calculations",
        "Straddle break-even points",
        "Beta hedging formula",
        "Futures pricing (Cost of Carry)",
        "The Greeks interpretations",
        "Margin call calculations"
      ]
    }
  ];

  const additionalResources = [
    {
      title: "CIRO Exam Requirements & Eligibility",
      slug: "ciro-exam-requirements-eligibility",
      description: "Registration and prerequisites"
    },
    {
      title: "CIRO Exam Cost & Fees Guide",
      slug: "ciro-exam-cost-fees-guide",
      description: "Complete pricing breakdown"
    },
    {
      title: "CIRO Exam Pass Rates & Difficulty",
      slug: "ciro-exam-pass-rates-difficulty",
      description: "What to expect on the exam"
    },
    {
      title: "Investment Career Path in Canada",
      slug: "investment-career-path-canada",
      description: "From entry to senior advisor"
    },
    {
      title: "Financial Services Career Roadmap",
      slug: "financial-services-career-roadmap-blueprint",
      description: "Complete career blueprint"
    }
  ];

  return (
    <>
      <SEO 
        title="Derivatives Exam Study Guide | Free Learning Path for Options, Futures & Swaps | Vitta Finance Academy"
        description="Master the CIRO Derivatives Exam with our structured 10-week study guide covering all 8 elements. Free learning path with options mechanics, The Greeks, trading strategies, hedging, and clearing/settlement. Pass on your first attempt."
        keywords="Derivatives study guide, Derivatives exam preparation, options futures exam learning path, derivatives exam topics, derivatives free resources, The Greeks options, hedging strategies, CDCC clearing, derivatives trading strategies"
      />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        padding: '80px 20px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#d4af37',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            FREE STUDY RESOURCE
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            Derivatives Exam Study Guide
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.7'
          }}>
            Your complete 10-week learning path to pass the CIRO Derivatives Exam. 
            Master options, futures, The Greeks, trading strategies, and hedging techniques.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/derivatives-strategy-lab"
              data-testid="strategy-lab-btn"
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
              }}
            >
              📊 Strategy Lab (Interactive)
            </Link>
            <Link 
              to="/derivatives-exam"
              data-testid="derivatives-course-btn"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              View Derivatives Course
            </Link>
            <Link 
              to="/books"
              data-testid="study-materials-btn"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Get Study Materials
            </Link>
          </div>
        </div>
      </section>

      {/* Exam Overview */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '40px' }}>
            Derivatives Exam at a Glance
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '20px'
          }}>
            {[
              { label: 'Questions', value: '120', sublabel: 'Multiple Choice' },
              { label: 'Duration', value: '3 Hours', sublabel: 'Computer-Based' },
              { label: 'Passing Score', value: 'Set by CIRO', sublabel: 'Per Sitting' },
              { label: 'Study Time', value: '8-10 Weeks', sublabel: 'Recommended' },
              { label: 'Elements', value: '8 Topics', sublabel: 'Comprehensive' },
              { label: 'Largest Section', value: '22%', sublabel: 'Trading Strategies' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e3a8a' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginTop: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '3px' }}>{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Element Weightings */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Derivatives Exam Element Weightings
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {[
              { element: 'Element 6', name: 'Trading Strategies', weight: '22%', questions: '~26', priority: 'high' },
              { element: 'Element 3', name: 'Types & Features', weight: '18%', questions: '~22', priority: 'high' },
              { element: 'Element 4', name: 'Derivative Pricing', weight: '18%', questions: '~22', priority: 'high' },
              { element: 'Element 5', name: 'Clearing & Settlement', weight: '17%', questions: '~20', priority: 'medium' },
              { element: 'Element 2', name: 'Documentation', weight: '8%', questions: '~10', priority: 'medium' },
              { element: 'Element 1', name: 'Client Relationship', weight: '7%', questions: '~8', priority: 'low' },
              { element: 'Element 7', name: 'Market Integrity', weight: '5%', questions: '~6', priority: 'low' },
              { element: 'Element 8', name: 'Standards of Conduct', weight: '5%', questions: '~6', priority: 'low' }
            ].map((el, index) => (
              <div key={index} style={{
                background: el.priority === 'high' ? 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)' : 'white',
                color: el.priority === 'high' ? '#1e3a8a' : '#1e3a8a',
                padding: '18px',
                borderRadius: '10px',
                border: el.priority === 'high' ? 'none' : '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>{el.weight}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '2px' }}>{el.questions} questions</div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  marginTop: '10px',
                  borderTop: `1px solid ${el.priority === 'high' ? 'rgba(30,58,138,0.2)' : '#e5e7eb'}`,
                  paddingTop: '10px'
                }}>
                  {el.name}
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '20px', fontSize: '0.9rem' }}>
            <strong>Focus on Elements 3, 4, and 6</strong> - they account for 58% of the exam
          </p>
        </div>
      </section>

      {/* Recommended Resources */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                📚
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#1e3a8a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #1e3a8a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: '#1e3a8a',
                fontSize: '1.5rem'
              }}>
                🎓
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Video lessons, AI tutor, practice questions, mock exams, and expert instruction.
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>/month</span>
              </div>
              <a 
                href={studyMaterials.course.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#d4af37',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Enroll Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '15px' }}>
            Structured Learning Path
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
            Follow this 9-phase study plan to systematically master all Derivatives exam content
          </p>

          {learningPath.map((phase, phaseIndex) => (
            <div key={phaseIndex} style={{ marginBottom: '35px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: phase.highlight 
                    ? 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)'
                    : 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                  color: phase.highlight ? '#1e3a8a' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {phaseIndex + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', margin: '0 0 3px' }}>{phase.phase}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                    {phase.description} • <span style={{ color: '#d4af37', fontWeight: '600' }}>{phase.duration}</span>
                  </p>
                </div>
              </div>

              <div style={{
                marginLeft: '22px',
                borderLeft: phaseIndex < learningPath.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingLeft: '35px',
                paddingBottom: phaseIndex < learningPath.length - 1 ? '15px' : '0'
              }}>
                {/* Articles */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: phase.articles.length > 1 ? 'repeat(auto-fit, minmax(250px, 1fr))' : '1fr',
                  gap: '12px',
                  marginBottom: phase.topics ? '15px' : '0'
                }}>
                  {phase.articles.map((article, articleIndex) => (
                    <Link 
                      key={articleIndex}
                      to={`/blog/${article.slug}`}
                      data-testid={`article-${article.slug}`}
                      style={{
                        background: 'white',
                        padding: '16px 18px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        border: phase.highlight ? '2px solid #d4af37' : '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#1e3a8a';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = phase.highlight ? '#d4af37' : '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <h4 style={{ 
                        fontSize: '0.95rem', 
                        color: '#1e3a8a', 
                        margin: '0 0 6px',
                        lineHeight: '1.4'
                      }}>
                        {article.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.8rem', 
                        color: '#6b7280', 
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        {article.description}
                      </p>
                    </Link>
                  ))}
                </div>

                {/* Topics covered */}
                {phase.topics && (
                  <div style={{
                    background: 'white',
                    padding: '15px 18px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '10px', fontWeight: '600' }}>
                      Key Topics Covered:
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {phase.topics.map((topic, topicIndex) => (
                        <span key={topicIndex} style={{
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Study Pathways */}
      <VittaDerivativesPathways />

      {/* Additional Resources */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Additional Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
          }}>
            {additionalResources.map((resource, index) => (
              <Link 
                key={index}
                to={`/blog/${resource.slug}`}
                style={{
                  background: 'white',
                  padding: '18px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#d4af37'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#d4af37',
                  flexShrink: 0
                }} />
                <div>
                  <div style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: '600' }}>{resource.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{resource.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Study Resources */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                📚
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#1e3a8a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Interactive Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #8b5cf6',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700'
              }}>NEW</div>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                ⚡
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.interactiveBook.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.interactiveBook.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.interactiveBook.price}
                </span>
              </div>
              <Link 
                to={studyMaterials.interactiveBook.link}
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Explore Interactive Book
              </Link>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #1e3a8a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: '#1e3a8a',
                fontSize: '1.5rem'
              }}>
                🎓
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Video lessons, AI tutor, practice questions, mock exams, and expert instruction.
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>/month</span>
              </div>
              <a 
                href={studyMaterials.course.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#d4af37',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Enroll Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Book CTA */}
      <DerivativesInteractiveBookCTA />

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Ready to Master Derivatives?</h2>
          <p style={{ opacity: '0.9', marginBottom: '30px', fontSize: '1.1rem' }}>
            Get comprehensive prep with our Derivatives course featuring video lessons, practice questions, and full mock exams.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-derivatives-course"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}
            >
              Explore Derivatives Course - $29.99/month
            </a>
            <Link 
              to="/exam-comparison"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Compare All CIRO Exams
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DerivativesStudyGuide;
